// Constantes centralisées pour les statuts de chantiers

export const STATUTS_CHANTIER = {
  BROUILLON: 'brouillon',
  DEVIS_ENVOYE: 'devis_envoye',
  PLANIFIE: 'planifie',
  EN_COURS: 'en_cours',
  SUSPENDU: 'suspendu',
  TERMINE: 'termine',
  ANNULE: 'annule',
  ARCHIVE: 'archive',
} as const;

export type StatutChantier = typeof STATUTS_CHANTIER[keyof typeof STATUTS_CHANTIER];

// Configuration des statuts avec labels, couleurs et icônes
export const STATUTS_CONFIG: Record<string, { label: string; color: string; icon: string }> = {
  'brouillon': {
    label: 'Brouillon',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
    icon: '📝'
  },
  'devis_envoye': {
    label: 'Devis envoyé',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    icon: '📊'
  },
  'planifie': {
    label: 'Planifié',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    icon: '📅'
  },
  'en_cours': {
    label: 'En cours',
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    icon: '🏗️'
  },
  'suspendu': {
    label: 'Suspendu',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    icon: '⏸️'
  },
  'termine': {
    label: 'Terminé',
    color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
    icon: '✅'
  },
  'annule': {
    label: 'Annulé',
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    icon: '❌'
  },
  'archive': {
    label: 'Archivé',
    color: 'bg-slate-100 text-slate-800 dark:bg-slate-800 dark:text-slate-300',
    icon: '📦'
  },
};

// Liste ordonnée pour les selects/dropdowns (sans archivé qui est géré séparément)
export const STATUTS_LIST = [
  STATUTS_CHANTIER.BROUILLON,
  STATUTS_CHANTIER.DEVIS_ENVOYE,
  STATUTS_CHANTIER.PLANIFIE,
  STATUTS_CHANTIER.EN_COURS,
  STATUTS_CHANTIER.SUSPENDU,
  STATUTS_CHANTIER.TERMINE,
  STATUTS_CHANTIER.ANNULE,
];

// Helper pour obtenir la config d'un statut
export function getStatutConfig(statut: string) {
  return STATUTS_CONFIG[statut] || STATUTS_CONFIG[STATUTS_CHANTIER.BROUILLON];
}

// ═══════════════════════════════════════════════════════════════════════════
// TRANSITIONS DE STATUTS - Règles métier
// ═══════════════════════════════════════════════════════════════════════════

// Transitions autorisées par statut actuel
// Format : statut_actuel -> [statuts_cibles_possibles]
export const TRANSITIONS_AUTORISEES: Record<StatutChantier, StatutChantier[]> = {
  brouillon: ['devis_envoye', 'planifie', 'en_cours', 'annule'],
  devis_envoye: ['brouillon', 'planifie', 'en_cours', 'annule'],
  planifie: ['brouillon', 'devis_envoye', 'en_cours', 'annule'],
  en_cours: ['suspendu', 'termine', 'annule'], // Tous via modals
  suspendu: ['en_cours', 'termine', 'annule'], // Tous via modals
  termine: ['en_cours'], // Réouverture uniquement
  annule: [], // Pas de réactivation (ModalReactivationChantier n'existe pas)
  archive: [], // Pas de transition depuis archivé
};

// Transitions nécessitant un modal spécifique
// Format : "statut_source->statut_cible" -> nom_du_modal
export const TRANSITIONS_AVEC_MODAL: Record<string, string> = {
  'en_cours->suspendu': 'ModalSuspensionChantier',
  'en_cours->termine': 'ChantierClosureDialog',
  'en_cours->annule': 'ModalAnnulationChantier',
  'suspendu->en_cours': 'ModalRepriseChantier',
  'suspendu->termine': 'ChantierClosureDialog',
  'suspendu->annule': 'ModalAnnulationChantier',
  // termine->en_cours : transition directe (pas de modal)
};

// Labels descriptifs pour chaque transition
export const TRANSITIONS_LABELS: Record<string, { label: string; description: string }> = {
  // Depuis Brouillon
  'brouillon->devis_envoye': { label: 'Envoyer le devis', description: 'Le devis a été envoyé au client' },
  'brouillon->planifie': { label: 'Valider le chantier', description: 'Le chantier est validé et prêt à démarrer' },
  'brouillon->en_cours': { label: 'Démarrer les travaux', description: 'Les travaux ont commencé' },
  'brouillon->annule': { label: 'Annuler le projet', description: 'Le projet est abandonné' },

  // Depuis Devis envoyé
  'devis_envoye->brouillon': { label: 'Revenir en brouillon', description: 'Modifier le chantier avant de renvoyer' },
  'devis_envoye->planifie': { label: 'Devis accepté', description: 'Le client a validé le devis' },
  'devis_envoye->en_cours': { label: 'Démarrer immédiatement', description: 'Les travaux démarrent maintenant' },
  'devis_envoye->annule': { label: 'Client a refusé', description: 'Le client a refusé le devis' },

  // Depuis Planifié
  'planifie->brouillon': { label: 'Revenir en brouillon', description: 'Annuler la planification' },
  'planifie->devis_envoye': { label: 'Renégocier', description: 'Renvoyer un nouveau devis' },
  'planifie->en_cours': { label: 'Démarrer les travaux', description: 'Lancer le chantier' },
  'planifie->annule': { label: 'Annuler le chantier', description: 'Abandonner le projet' },

  // Depuis En cours
  'en_cours->suspendu': { label: 'Suspendre', description: 'Mettre le chantier en pause temporaire' },
  'en_cours->termine': { label: 'Terminer', description: 'Clôturer le chantier' },
  'en_cours->annule': { label: 'Abandonner', description: 'Arrêter définitivement le chantier' },

  // Depuis Suspendu
  'suspendu->en_cours': { label: 'Reprendre', description: 'Reprendre les travaux' },
  'suspendu->termine': { label: 'Clôturer', description: 'Terminer avec travaux partiels' },
  'suspendu->annule': { label: 'Abandonner', description: 'Arrêter définitivement' },

  // Depuis Terminé
  'termine->en_cours': { label: 'Rouvrir', description: 'Travaux supplémentaires nécessaires' },

};

// Interface pour une option de transition
export interface TransitionOption {
  statut: StatutChantier;
  label: string;
  description: string;
  modal?: string;
  warning?: string;
  disabled?: boolean;
  disabledReason?: string;
}

// Helper : obtenir les transitions possibles avec logique de dates
export function getTransitionsPossibles(
  statutActuel: StatutChantier,
  dateDebutPrevue?: string | null,
  dateDebutReelle?: string | null,
  dateFinPrevue?: string | null,
  dateFinReelle?: string | null
): TransitionOption[] {
  const transitionsAutorisees = TRANSITIONS_AUTORISEES[statutActuel] || [];
  const today = new Date().toISOString().split('T')[0];

  // Utiliser date réelle si disponible, sinon date prévue
  const dateDebut = dateDebutReelle || dateDebutPrevue;

  return transitionsAutorisees.map((statutCible) => {
    const key = `${statutActuel}->${statutCible}`;
    const labelConfig = TRANSITIONS_LABELS[key] || {
      label: STATUTS_CONFIG[statutCible]?.label || statutCible,
      description: ''
    };
    const modal = TRANSITIONS_AVEC_MODAL[key];

    let warning: string | undefined;
    let disabled = false;
    let disabledReason: string | undefined;

    // Logique de dates pour certaines transitions
    if (statutCible === 'en_cours') {
      // Warning si on démarre avant la date prévue
      if (dateDebut && dateDebut > today) {
        warning = `Le chantier ne devait pas commencer avant le ${formatDateFr(dateDebut)}`;
      }
    }

    // Pour brouillon/devis_envoye → planifié : date_debut doit être définie
    if ((statutActuel === 'brouillon' || statutActuel === 'devis_envoye') && statutCible === 'planifie') {
      if (!dateDebut) {
        disabled = true;
        disabledReason = 'Définissez d\'abord une date de début';
      }
    }

    return {
      statut: statutCible,
      label: labelConfig.label,
      description: labelConfig.description,
      modal,
      warning,
      disabled,
      disabledReason,
    };
  });
}

// Helper : vérifier si une transition est autorisée
export function isTransitionAutorisee(
  statutActuel: StatutChantier,
  statutCible: StatutChantier
): boolean {
  const transitions = TRANSITIONS_AUTORISEES[statutActuel] || [];
  return transitions.includes(statutCible);
}

// Helper : obtenir le modal requis pour une transition
export function getModalForTransition(
  statutActuel: StatutChantier,
  statutCible: StatutChantier
): string | undefined {
  const key = `${statutActuel}->${statutCible}`;
  return TRANSITIONS_AVEC_MODAL[key];
}

// Helper : formater une date en français (JJ/MM/AAAA)
function formatDateFr(dateStr: string): string {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
}

// ═══════════════════════════════════════════════════════════════════════════
// STATUTS À LA CRÉATION - Restrictions basées sur les dates
// ═══════════════════════════════════════════════════════════════════════════

export interface StatutCreationOption {
  value: string;
  label: string;
  emoji: string;
  disabled: boolean;
  reason?: string;
}

/**
 * Retourne les statuts disponibles à la création d'un chantier
 * en fonction des dates sélectionnées.
 *
 * Règles :
 * - Brouillon, Devis envoyé, Planifié : toujours disponibles
 * - En cours : disponible si date_debut <= aujourd'hui + 7 jours
 * - Suspendu : jamais disponible à la création
 * - Terminé : disponible si date_fin <= aujourd'hui (saisie a posteriori)
 * - Annulé : jamais disponible à la création
 */
export function getStatutsDisponiblesCreation(
  dateDebutPrevue: string | null | undefined,
  dateFinEstimee: string | null | undefined
): StatutCreationOption[] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Normaliser les dates à minuit pour éviter les problèmes de timezone
  let dateDebut: Date | null = null;
  if (dateDebutPrevue) {
    dateDebut = new Date(dateDebutPrevue);
    dateDebut.setHours(0, 0, 0, 0);
  }

  let dateFin: Date | null = null;
  if (dateFinEstimee) {
    dateFin = new Date(dateFinEstimee);
    dateFin.setHours(0, 0, 0, 0);
  }

  // Seuil pour "En cours" : 7 jours dans le futur
  const seuilEnCours = new Date(today);
  seuilEnCours.setDate(seuilEnCours.getDate() + 7);

  // Calcul des conditions
  const enCoursDisabled = !dateDebut || dateDebut > seuilEnCours;
  const termineDisabled = !dateFin || dateFin > today;

  // "Planifié" désactivé si date_debut <= aujourd'hui (le chantier devrait être "En cours")
  const planifieDisabled = dateDebut && dateDebut.getTime() <= today.getTime();

  if (import.meta.env.DEV) console.log('[getStatutsDisponiblesCreation]', {
    dateDebutPrevue,
    dateDebut: dateDebut?.toISOString(),
    today: today.toISOString(),
    planifieDisabled,
    enCoursDisabled,
    comparison: dateDebut ? `${dateDebut.getTime()} <= ${today.getTime()} = ${dateDebut.getTime() <= today.getTime()}` : 'no date'
  });

  const statuts: StatutCreationOption[] = [
    {
      value: 'brouillon',
      label: 'Brouillon',
      emoji: '📝',
      disabled: false
    },
    {
      value: 'devis_envoye',
      label: 'Devis envoyé',
      emoji: '📄',
      disabled: false
    },
    {
      value: 'planifie',
      label: 'Planifié',
      emoji: '📋',
      disabled: !!planifieDisabled,
      reason: planifieDisabled
        ? 'La date de début est passée ou aujourd\'hui, utilisez "En cours"'
        : undefined
    },
    {
      value: 'en_cours',
      label: 'En cours',
      emoji: '🚧',
      disabled: enCoursDisabled,
      reason: !dateDebut
        ? 'Définissez une date de début pour activer ce statut'
        : dateDebut > seuilEnCours
          ? `Disponible quand la date de début sera dans moins de 7 jours`
          : undefined
    },
    {
      value: 'suspendu',
      label: 'Suspendu',
      emoji: '⏸️',
      disabled: true,
      reason: 'Un nouveau chantier ne peut pas être créé suspendu'
    },
    {
      value: 'termine',
      label: 'Terminé',
      emoji: '✅',
      disabled: termineDisabled,
      reason: !dateFin
        ? 'Définissez une date de fin passée pour ce statut'
        : dateFin > today
          ? 'La date de fin doit être passée pour créer un chantier terminé'
          : undefined
    },
    {
      value: 'annule',
      label: 'Annulé',
      emoji: '❌',
      disabled: true,
      reason: 'Un nouveau chantier ne peut pas être créé annulé'
    },
  ];

  return statuts;
}

// ═══════════════════════════════════════════════════════════════════════════
// ARCHIVAGE - Règles métier
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Vérifie si un chantier peut être archivé.
 * Seuls les chantiers brouillon, terminés ou annulés peuvent être archivés.
 * Les chantiers "actifs" (devis_envoye, planifie, en_cours, suspendu) ne peuvent pas être archivés.
 */
export function canBeArchived(etatChantier: StatutChantier): boolean {
  return etatChantier === 'brouillon' || etatChantier === 'termine' || etatChantier === 'annule';
}

/**
 * Vérifie si un chantier peut être désarchivé.
 */
export function canBeUnarchived(etatChantier: StatutChantier): boolean {
  return etatChantier === 'archive';
}

/**
 * Statuts possibles lors du désarchivage d'un chantier.
 * L'utilisateur choisit vers quel statut revenir.
 */
export const STATUTS_DESARCHIVAGE: { value: StatutChantier; label: string; description: string }[] = [
  { value: 'brouillon', label: 'Brouillon', description: 'Pour retravailler le projet' },
  { value: 'planifie', label: 'Planifié', description: 'Pour reprendre les travaux' },
];

// ═══════════════════════════════════════════════════════════════════════════
// STATUTS POUR LE MODAL D'AFFECTATION (Planning)
// Seuls les statuts pertinents pour créer/modifier des affectations
// ═══════════════════════════════════════════════════════════════════════════

export const STATUTS_AFFECTATION_MODAL: { key: StatutChantier; defaultOpen: boolean }[] = [
  { key: 'en_cours', defaultOpen: true },
  { key: 'planifie', defaultOpen: true },
  { key: 'suspendu', defaultOpen: false },
];
